import { useEffect, useRef, useState } from "react";
import type { Akcent } from "@gierki/shared";
import { Karta, Przycisk } from "../ui.tsx";
import { Kostka } from "./Kostka.tsx";

/**
 * Osadnicy z Catanu — plansza i panel gracza.
 *
 * Plansza rysowana z geometrii przysłanej przez serwer, z tych samych stałych,
 * na których stoją reguły. Dotykalne są wyłącznie miejsca podane przez serwer:
 * telefon nigdy nie decyduje, co wolno.
 *
 * Panel jest zbudowany wokół jednego pytania — „co mogę teraz zrobić". Stąd
 * **tryb budowy**: dopóki nie wybierzesz, czy stawiasz drogę, osadę czy miasto,
 * plansza nie świeci się od celów, których i tak nie chciałeś. Bez tego trzy
 * rodzaje celów nakładały się na siebie i nie było wiadomo, w co się celuje.
 */

export interface CatanView {
  /** Angielska nazwa, bo `Game.tsx` czyta ją generycznie i odpala na niej haptykę. */
  phase: "lobby" | "rozstawianie" | "rzut" | "akcje" | "odrzucanie" | "zlodziej" | "koniec";
  hostId: string;
  players: string[];
  kolory: Record<string, Akcent>;
  celPZ: number;
  naRuchu: string | null;
  mojaTura: boolean;

  teren: string[];
  liczby: (number | null)[];
  porty: Array<{ krawedz: number; surowiec: string | null; stosunek: number }>;
  zlodziej: number;
  osady: Array<{ w: number; gracz: string; miasto: boolean }>;
  drogi: Array<{ k: number; gracz: string }>;

  geometria: {
    promien: number;
    heksy: Array<{ x: number; y: number }>;
    wierzcholki: Array<{ x: number; y: number }>;
    krawedzie: Array<[number, number]>;
    heksWierzcholki: number[][];
  };

  celeOsad: number[];
  celeDrog: number[];
  celeMiast: number[];
  celeZlodzieja: number[];
  ofiaryPolem: Record<string, string[]>;
  /** Ile dróg z karty „Budowa dróg" zostało do postawienia za darmo. */
  darmoweDrogi: number;

  doOdrzucenia: Record<string, number>;
  muszeOdrzucic: number;

  mojeKarty: string[];
  kartyRozwojuGraczy: Record<string, number>;
  taliaZostalo: number;
  rycerze: Record<string, number>;
  najdluzszaDrogaU: string | null;
  /** Długość najdłuższej drogi każdego gracza — liczona przez serwer. */
  dlugoscDrogi: Record<string, number>;
  najwiekszaArmiaU: string | null;
  kursy: Record<string, number>;
  przekazDoMnie: { od: string; surowce: Record<string, number> } | null;
  punkty: Record<string, number>;

  kosci: [number, number] | null;
  bank: Record<string, number>;
  koszty: Record<"droga" | "osada" | "miasto", Record<string, number>>;
  kosztRozwoju: Record<string, number>;

  mojaReka: Record<string, number>;
  kartyGraczy: Record<string, number>;
  canStart: boolean;
}

interface Props {
  view: CatanView;
  nicki: Record<string, string>;
  mojeId: string;
  onStart: () => void;
  onOsada: (w: number) => void;
  onDroga: (k: number) => void;
  onRzut: () => void;
  onBuduj: (co: "droga" | "osada" | "miasto", gdzie: number) => void;
  onHandel: (oddaje: string, bierze: string) => void;
  onKoniecTury: () => void;
  onOdrzuc: (karty: Record<string, number>) => void;
  onZlodziej: (heks: number, ofiara: string | null) => void;
  onKupRozwoj: () => void;
  onZagrajRozwoj: (karta: string, opcje?: Record<string, unknown>) => void;
  onPrzyjmij: () => void;
}

/** Barwy terenu — tokeny z `index.css`, jeden komplet dla obu motywów. */
const KOLOR_TERENU: Record<string, string> = {
  las: "var(--teren-las)",
  pastwisko: "var(--teren-pastwisko)",
  pole: "var(--teren-pole)",
  wzgorza: "var(--teren-wzgorza)",
  gory: "var(--teren-gory)",
  pustynia: "var(--teren-pustynia)",
};

/**
 * Tereny, dla których leży tekstura w `web/public/teren/`.
 *
 * Lista jawna, a nie próba wczytania wszystkiego: brakujący plik daje 404
 * w konsoli przy każdym renderze planszy, a pole bez tekstury i tak wygląda
 * poprawnie — zostaje samo wypełnienie tokenem.
 */
const MAJA_TEKSTURE = new Set(["las", "pastwisko", "pole", "wzgorza", "gory", "pustynia"]);

/**
 * Ile tekstury w ogóle widać.
 *
 * Tekstura ma być fakturą pola, a nie jego treścią: liczba na żetonie i pionki
 * graczy są ważniejsze. Przy pełnej mocy plansza robiła się ciemna i ciężka.
 */
const KRYCIE_TEKSTURY = 0.5;

/**
 * Siła wzmocnienia tekstury, osobno dla każdego terenu.
 *
 * Wykładnik gammy: przyciemnia półcienie, zostawiając biel bielą — dokładnie
 * to, czego potrzebuje `multiply`. Jedna wartość dla wszystkich nie działa,
 * bo pliki wyszły w różnych jasnościach.
 *
 * Wartości są celowo niskie. Pierwsze podejście (9–12) wyglądało dobrze
 * w oderwaniu od planszy, ale te pliki mają **szare tło wewnątrz heksu**, nie
 * przezroczyste — mocna gamma przyciemniała więc całe pole, nie sam rysunek,
 * i plansza zrobiła się czarna.
 */
const MOC_TEKSTURY: Record<string, number> = {
  las: 1.4,
  pastwisko: 3,
  pole: 3,
  wzgorza: 3.5,
  gory: 2.4,
  pustynia: 3.5,
};

/** Kolejność stała, żeby karty na ręce nie skakały między turami. */
const SUROWCE = ["drewno", "glina", "welna", "zboze", "ruda"] as const;

const SKROT: Record<string, string> = {
  drewno: "Drewno",
  glina: "Glina",
  welna: "Wełna",
  zboze: "Zboże",
  ruda: "Ruda",
};

/**
 * Jedna litera na surowiec — do cenników, gdzie nie mieści się nazwa.
 *
 * Pierwsze litery pięciu surowców są różne, więc skrót jest jednoznaczny
 * i nie trzeba go tłumaczyć.
 */
const LITERA: Record<string, string> = {
  drewno: "D",
  glina: "G",
  welna: "W",
  zboze: "Z",
  ruda: "R",
};

const NAZWA_KARTY: Record<string, string> = {
  rycerz: "Rycerz",
  "budowa-drog": "Budowa dróg",
  monopol: "Monopol",
  urodzaj: "Urodzaj",
  punkt: "Punkt zwycięstwa",
};

/** Karty, które przed zagraniem wymagają wskazania surowca. */
const WYMAGA_WYBORU = new Set(["monopol", "urodzaj"]);

type Budowla = "droga" | "osada" | "miasto";
const BUDOWLE: Budowla[] = ["droga", "osada", "miasto"];
const NAZWA_BUDOWLI: Record<Budowla, string> = {
  droga: "Droga",
  osada: "Osada",
  miasto: "Miasto",
};

/** Pole dotyku wokół wierzchołka. Dużo większe niż znacznik — celujemy w aucie. */
const DOTYK = 3.2;

const suma = (karty: Record<string, number>): number =>
  Object.values(karty).reduce((a, b) => a + b, 0);

/** Jedno zdanie u góry: co teraz. */
function status(view: CatanView, nicki: Record<string, string>): string {
  if (view.phase === "lobby") return "Czekamy na start.";
  if (view.phase === "koniec") return "Koniec partii.";

  // Odrzucanie wyprzedza wszystko: dotyczy też tych, którzy nie są na ruchu.
  if (view.muszeOdrzucic > 0) return `Wypadła siódemka — odrzuć ${view.muszeOdrzucic} kart.`;
  if (view.phase === "odrzucanie") return "Czekamy, aż pozostali odrzucą karty.";
  if (view.phase === "zlodziej") {
    return view.mojaTura ? "Dotknij pola, na które wstawiasz złodzieja." : "Ruch złodzieja.";
  }

  if (!view.mojaTura) return `Ruch: ${nicki[view.naRuchu ?? ""] ?? "?"}`;
  if (view.phase === "akcje" && view.darmoweDrogi > 0) {
    return view.darmoweDrogi === 2
      ? "Budowa dróg — dotknij dwóch podświetlonych krawędzi."
      : "Budowa dróg — została jeszcze jedna droga.";
  }
  if (view.phase === "rozstawianie") {
    return view.celeOsad.length > 0 ? "Postaw osadę." : "Postaw drogę przy nowej osadzie.";
  }
  if (view.phase === "rzut") return "Twoja tura — rzuć kośćmi.";
  return "Twoja tura.";
}

/**
 * Cennik: kropka w barwie surowca i liczba sztuk.
 *
 * Wcześniej stały tu same kropki po dwa piksele, jedna na sztukę — z fotela
 * obok nie dawały się ani policzyć, ani rozpoznać po barwie. Surowca, którego
 * brakuje na ręce, nie chowamy: pokazujemy go przekreślonego, bo pytanie brzmi
 * „czego mi brakuje", a nie „czy mnie stać".
 */
function Koszt({ koszt, reka }: { koszt: Record<string, number>; reka: Record<string, number> }) {
  return (
    <span className="flex flex-wrap justify-center gap-x-2 gap-y-1 text-xs tabular-nums">
      {SUROWCE.filter((surowiec) => (koszt[surowiec] ?? 0) > 0).map((surowiec) => {
        const brakuje = (reka[surowiec] ?? 0) < (koszt[surowiec] as number);
        return (
          <span
            key={surowiec}
            className={["flex items-center gap-1", brakuje ? "line-through opacity-60" : ""].join(
              " ",
            )}
          >
            <span
              className="h-3 w-3 rounded-full border border-obrys"
              style={{ backgroundColor: `var(--surowiec-${surowiec})` }}
            />
            {LITERA[surowiec]}
            {(koszt[surowiec] as number) > 1 ? `×${koszt[surowiec]}` : ""}
          </span>
        );
      })}
    </span>
  );
}

/**
 * Ile czego przybyło od ostatniego widoku.
 *
 * Liczymy z różnicy rąk, a nie z rzutu: tak samo widać przyrost z produkcji,
 * z portu, z monopolu i z urodzaju — a pytanie gracza brzmi „co mi przyszło",
 * nie „skąd". Ubytki pomijamy: co zniknęło, widać po tym, że karty nie ma,
 * a migający minus przy każdej budowie byłby hałasem.
 *
 * `licznik` rośnie przy każdym przyroście i służy wyłącznie jako klucz
 * elementu — bez niego druga identyczna dostawa nie odpaliłaby animacji.
 */
function usePrzyrostSurowcow(reka: Record<string, number>): {
  ile: Record<string, number>;
  licznik: number;
} {
  const [przyrost, setPrzyrost] = useState<{ ile: Record<string, number>; licznik: number }>({
    ile: {},
    licznik: 0,
  });
  const poprzednia = useRef<Record<string, number> | null>(null);

  useEffect(() => {
    const przed = poprzednia.current;
    poprzednia.current = reka;
    // Pierwszy widok po wejściu do partii nie jest przyrostem — inaczej
    // powrót z tunelu wyglądałby jak dostawa całej ręki naraz.
    if (!przed) return;

    const ile: Record<string, number> = {};
    for (const surowiec of SUROWCE) {
      const roznica = (reka[surowiec] ?? 0) - (przed[surowiec] ?? 0);
      if (roznica > 0) ile[surowiec] = roznica;
    }
    if (Object.keys(ile).length === 0) return;

    setPrzyrost((stary) => ({ ile, licznik: stary.licznik + 1 }));
  }, [reka]);

  // Napis znika sam; dłużej niż animacja, żeby nie urwać jej w połowie.
  useEffect(() => {
    if (przyrost.licznik === 0) return;
    const timer = setTimeout(() => setPrzyrost((stary) => ({ ...stary, ile: {} })), 1300);
    return () => clearTimeout(timer);
  }, [przyrost.licznik]);

  return przyrost;
}

/**
 * Karta surowca: pasek w barwie terenu, nazwa i duża liczba.
 *
 * `przyrost` to liczba kart, które właśnie przybyły — pokazujemy ją nad
 * licznikiem, w barwie zależnej od ilości (patrz `--przyrost-*`). Klucz
 * elementu niesie licznik zdarzeń, żeby animacja odpaliła też wtedy, gdy
 * przyrost jest ten sam co poprzednio: „+1 zboże" dwa razy z rzędu przy
 * porównywaniu wartości wyglądałoby jak „nic się nie stało".
 */
function KartaSurowca({
  surowiec,
  ile,
  przyrost = 0,
}: {
  surowiec: string;
  ile: number;
  przyrost?: number;
}) {
  return (
    <div
      className={[
        "relative flex flex-col items-center rounded-xl border border-obrys px-1 py-2",
        ile > 0 ? "bg-panel-jasny" : "bg-transparent opacity-40",
      ].join(" ")}
    >
      {przyrost > 0 && (
        <span
          className="przyrost pointer-events-none absolute inset-x-0 bottom-1 text-center text-2xl font-bold tabular-nums"
          style={
            {
              "--przyrost-kolor": `var(--przyrost-${Math.min(przyrost, 5)})`,
              // Skala rośnie z ilością, ale nie w nieskończoność — przy „+8"
              // napis wychodziłby poza kartę i zasłaniał sąsiadów.
              "--przyrost-skala": String(1.4 + Math.min(przyrost, 5) * 0.12),
            } as React.CSSProperties
          }
        >
          +{przyrost}
        </span>
      )}
      <span
        className="h-2 w-full rounded-full"
        style={{ backgroundColor: `var(--surowiec-${surowiec})` }}
      />
      <span className="mt-1 text-xs text-tekst-slaby">{SKROT[surowiec] ?? surowiec}</span>
      <span className="text-2xl tabular-nums">{ile}</span>
    </div>
  );
}

export function Catan({
  view,
  nicki,
  mojeId,
  onStart,
  onOsada,
  onDroga,
  onRzut,
  onBuduj,
  onHandel,
  onKoniecTury,
  onOdrzuc,
  onZlodziej,
  onKupRozwoj,
  onZagrajRozwoj,
  onPrzyjmij,
}: Props) {
  const { geometria: g } = view;
  const [wybrane, setWybrane] = useState<Record<string, number>>({});
  const [wyborOfiary, setWyborOfiary] = useState<{ heks: number; kandydaci: string[] } | null>(
    null,
  );
  const [wyborKarty, setWyborKarty] = useState<string | null>(null);
  const [tryb, setTryb] = useState<Budowla | null>(null);
  const [handelZ, setHandelZ] = useState<string | null>(null);
  /** Kto ma rozwinięty wiersz na liście graczy. Jeden naraz — ekran jest wąski. */
  const [rozwiniety, setRozwiniety] = useState<string | null>(null);
  /*
   * Kupowanie i wymiana schowane za przyciskami przy krawędzi planszy.
   * Rozłożone na płasko zajmowały pół ekranu, a używa się ich raz na kilka
   * tur — plansza jest tym, na co patrzy się przez całą partię.
   */
  const [panel, setPanel] = useState<"rozwoj" | "handel" | null>(null);

  const marginy = g.promien * 1.2;
  const xs = g.wierzcholki.map((p) => p.x);
  const ys = g.wierzcholki.map((p) => p.y);
  const minX = Math.min(...xs) - marginy;
  const minY = Math.min(...ys) - marginy;
  const szer = Math.max(...xs) - minX + marginy;
  const wys = Math.max(...ys) - minY + marginy;

  const barwa = (gracz: string): string => `var(--akcent-${view.kolory[gracz] ?? "zielony"})`;
  const rozstawianie = view.phase === "rozstawianie";

  /*
   * Środek planszy liczony z granic, a nie wpisany na sztywno: znaczniki portów
   * są od niego odsuwane na wodę i przy innym układzie współrzędnych wjechałyby
   * na ląd.
   */
  const srodekPlanszy = {
    x: (Math.min(...xs) + Math.max(...xs)) / 2,
    y: (Math.min(...ys) + Math.max(...ys)) / 2,
  };

  /*
   * W rozstawianiu trybu nie ma — serwer i tak podaje dokładnie jeden rodzaj
   * celu. W fazie akcji świecą się tylko cele wybranego trybu.
   */
  const widoczneOsady = rozstawianie || tryb === "osada" ? view.celeOsad : [];
  /*
   * Darmowe drogi z karty świecą **bez** wchodzenia w tryb budowy: karta jest
   * już zagrana, więc pytanie „co budujesz" nie ma sensu — zostało tylko
   * „gdzie". Bez tego gracz widział komunikat i martwą planszę.
   */
  const darmowaDroga = view.darmoweDrogi > 0 && view.mojaTura && view.phase === "akcje";

  const przyrosty = usePrzyrostSurowcow(view.mojaReka);
  const widoczneDrogi = rozstawianie || darmowaDroga || tryb === "droga" ? view.celeDrog : [];
  const widoczneMiasta = tryb === "miasto" ? view.celeMiast : [];

  const doWyboru: Record<Budowla, number[]> = {
    droga: view.celeDrog,
    osada: view.celeOsad,
    miasto: view.celeMiast,
  };

  const kliknijOsade = (w: number) => {
    if (rozstawianie) onOsada(w);
    else onBuduj("osada", w);
    setTryb(null);
  };
  const kliknijDroge = (k: number) => {
    if (rozstawianie) onDroga(k);
    else onBuduj("droga", k);
    setTryb(null);
  };

  const wAkcji = view.mojaTura && view.phase === "akcje";

  return (
    <div className="flex flex-col gap-3">
      <div className="relative">
      <svg viewBox={`${minX} ${minY} ${szer} ${wys}`} className="w-full">
        {/* Maski pól — tekstura ma się kończyć dokładnie na krawędzi heksu. */}
        <defs>
          {/*
           * Wzmocnienie tekstury.
           *
           * Generowane pliki wychodzą bardzo jasne, a `multiply` przez biel nie
           * robi nic — motyw znikał pod kolorem pola. Gamma > 1 przyciemnia
           * półcienie, zostawiając biel bielą: rysunek się pojawia, a jaśniejszy
           * środek kafla dalej jest jaśniejszy i nie zabiera czytelności
           * żetonowi z liczbą.
           *
           * Wartość dobrana pod obecny komplet plików; ciemniejsze tekstury
           * będą wymagały jej zmniejszenia.
           */}
          {[...MAJA_TEKSTURE].map((teren) => {
            const moc = MOC_TEKSTURY[teren] ?? 4;
            return (
              <filter key={`f${teren}`} id={`teren-moc-${teren}`}>
                <feComponentTransfer>
                  <feFuncR type="gamma" exponent={moc} />
                  <feFuncG type="gamma" exponent={moc} />
                  <feFuncB type="gamma" exponent={moc} />
                </feComponentTransfer>
              </filter>
            );
          })}
          {view.teren.map((_t, heks) => (
            <clipPath key={`c${heks}`} id={`heks-${heks}`}>
              <polygon
                points={(g.heksWierzcholki[heks] as number[])
                  .map((w) => g.wierzcholki[w] as { x: number; y: number })
                  .map((p) => `${p.x},${p.y}`)
                  .join(" ")}
              />
            </clipPath>
          ))}
        </defs>

        {/* Heksy */}
        {view.teren.map((teren, heks) => {
          const rogi = g.heksWierzcholki[heks] as number[];
          const punkty = rogi
            .map((w) => g.wierzcholki[w] as { x: number; y: number })
            .map((p) => `${p.x},${p.y}`)
            .join(" ");
          const srodek = g.heksy[heks] as { x: number; y: number };
          const liczba = view.liczby[heks];
          const celZlodzieja = view.celeZlodzieja.includes(heks);
          return (
            <g key={`h${heks}`}>
              <polygon
                points={punkty}
                fill={KOLOR_TERENU[teren] ?? "var(--teren-pustynia)"}
                stroke={celZlodzieja ? "var(--color-akcent)" : "var(--plansza-obrys)"}
                strokeWidth={celZlodzieja ? 1 : 0.2}
                className={celZlodzieja ? "cel-pulsuje" : ""}
                onClick={() => {
                  if (!celZlodzieja) return;
                  // Jedna ofiara albo żadna — bierzemy od razu; przy kilku pytamy.
                  const kandydaci = view.ofiaryPolem[String(heks)] ?? [];
                  if (kandydaci.length <= 1) onZlodziej(heks, kandydaci[0] ?? null);
                  else setWyborOfiary({ heks, kandydaci });
                }}
              />
              {/*
                * Tekstura idzie NA wypełnienie tokenem, w trybie `multiply`:
                * szarość obrazka tylko przyciemnia barwę pola. Dzięki temu
                * kolory zostają w `index.css` i nie da się ich rozjechać
                * podmianą pliku PNG.
                */}
              {MAJA_TEKSTURE.has(teren) && (
                <image
                  href={`/teren/${teren}.png`}
                  x={srodek.x - (g.promien * Math.sqrt(3)) / 2}
                  y={srodek.y - g.promien}
                  width={g.promien * Math.sqrt(3)}
                  height={g.promien * 2}
                  preserveAspectRatio="xMidYMid slice"
                  clipPath={`url(#heks-${heks})`}
                  filter={`url(#teren-moc-${teren})`}
                  opacity={KRYCIE_TEKSTURY}
                  /*
                   * Tekstura przykrywa całe pole, więc bez `pointerEvents: none`
                   * przechwytuje dotknięcie zamiast wielokąta pod spodem —
                   * i złodzieja nie da się przestawić na żadne pole z fakturą,
                   * czyli na żadne w ogóle.
                   */
                  style={{ mixBlendMode: "multiply", pointerEvents: "none" }}
                />
              )}
              {liczba !== null && liczba !== undefined && (
                /* Żeton i pionek złodzieja też nie łapią dotknięć — leżą dokładnie
                   tam, gdzie celuje palec, gdy się wybiera pole. */
                <g style={{ pointerEvents: "none" }}>
                  <circle cx={srodek.x} cy={srodek.y} r={g.promien * 0.3} fill="var(--zeton-tlo)" />
                  <text
                    x={srodek.x}
                    y={srodek.y}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontSize={g.promien * 0.35}
                    /* Szóstka i ósemka na czerwono — tak samo jak na kartonie. */
                    fill={
                      liczba === 6 || liczba === 8
                        ? "var(--zeton-liczba-czerwona)"
                        : "var(--zeton-liczba)"
                    }
                  >
                    {liczba}
                  </text>
                </g>
              )}
              {heks === view.zlodziej && (
                <circle
                  cx={srodek.x}
                  cy={srodek.y}
                  r={g.promien * 0.22}
                  fill="var(--zlodziej)"
                  style={{ pointerEvents: "none" }}
                />
              )}
            </g>
          );
        })}

        {/*
          * Porty.
          *
          * Kurs liczy serwer (`porty.ts`), ale bez znacznika na planszy port jest
          * niewidzialny aż do otwarcia banku — a wtedy jest już za późno, bo
          * osadę stawia się kilka tur wcześniej. Znacznik siedzi na wodzie, przy
          * krawędzi portu, a dwa „mola" pokazują, **których wierzchołków** dotyczy:
          * to jedyna rzecz, którą trzeba wiedzieć, żeby zaplanować pod niego osadę.
          *
          * Cała grupa bez dotknięć — ozdoba nad polem klikalnym przechwytuje
          * kliknięcie i złodziej przestaje się dać przestawić.
          */}
        <g style={{ pointerEvents: "none" }}>
          {view.porty.map((port) => {
            const [a, b] = g.krawedzie[port.krawedz] as [number, number];
            const p = g.wierzcholki[a] as { x: number; y: number };
            const q = g.wierzcholki[b] as { x: number; y: number };
            const srodek = { x: (p.x + q.x) / 2, y: (p.y + q.y) / 2 };

            // Kierunek „na zewnątrz planszy" — od środka ku krawędzi.
            const dx = srodek.x - srodekPlanszy.x;
            const dy = srodek.y - srodekPlanszy.y;
            const dlugosc = Math.hypot(dx, dy) || 1;
            const cel = {
              x: srodek.x + (dx / dlugosc) * g.promien * 0.85,
              y: srodek.y + (dy / dlugosc) * g.promien * 0.85,
            };

            // Port „mój", gdy stoi przy nim moja osada — dokładnie ten warunek
            // liczy kurs na serwerze.
            const moj = view.osady.some(
              (osada) => osada.gracz === mojeId && (osada.w === a || osada.w === b),
            );
            const promienZnacznika = g.promien * 0.3;

            return (
              <g key={`p${port.krawedz}`}>
                <line
                  x1={p.x}
                  y1={p.y}
                  x2={cel.x}
                  y2={cel.y}
                  stroke="var(--plansza-obrys)"
                  strokeWidth={g.promien * 0.05}
                />
                <line
                  x1={q.x}
                  y1={q.y}
                  x2={cel.x}
                  y2={cel.y}
                  stroke="var(--plansza-obrys)"
                  strokeWidth={g.promien * 0.05}
                />
                <circle
                  cx={cel.x}
                  cy={cel.y}
                  r={promienZnacznika}
                  fill={
                    port.surowiec ? `var(--surowiec-${port.surowiec})` : "var(--zeton-tlo)"
                  }
                  stroke={moj ? "var(--color-akcent)" : "var(--plansza-obrys)"}
                  strokeWidth={moj ? g.promien * 0.07 : g.promien * 0.03}
                />
                {port.surowiec ? (
                  <>
                    <text
                      x={cel.x}
                      y={cel.y - g.promien * 0.09}
                      textAnchor="middle"
                      dominantBaseline="central"
                      fontSize={g.promien * 0.24}
                      fill="var(--zeton-liczba)"
                    >
                      {LITERA[port.surowiec] ?? "?"}
                    </text>
                    <text
                      x={cel.x}
                      y={cel.y + g.promien * 0.12}
                      textAnchor="middle"
                      dominantBaseline="central"
                      fontSize={g.promien * 0.2}
                      fill="var(--zeton-liczba)"
                    >
                      {port.stosunek}:1
                    </text>
                  </>
                ) : (
                  <text
                    x={cel.x}
                    y={cel.y}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontSize={g.promien * 0.24}
                    fill="var(--zeton-liczba)"
                  >
                    {port.stosunek}:1
                  </text>
                )}
              </g>
            );
          })}
        </g>

        {/* Postawione drogi */}
        {view.drogi.map((droga) => {
          const [a, b] = g.krawedzie[droga.k] as [number, number];
          const p = g.wierzcholki[a] as { x: number; y: number };
          const q = g.wierzcholki[b] as { x: number; y: number };
          return (
            <line
              key={`d${droga.k}`}
              x1={p.x}
              y1={p.y}
              x2={q.x}
              y2={q.y}
              stroke={barwa(droga.gracz)}
              strokeWidth={g.promien * 0.16}
              strokeLinecap="round"
            />
          );
        })}

        {/* Cele dróg — szeroki, półprzezroczysty pas dotyku */}
        {widoczneDrogi.map((k) => {
          const [a, b] = g.krawedzie[k] as [number, number];
          const p = g.wierzcholki[a] as { x: number; y: number };
          const q = g.wierzcholki[b] as { x: number; y: number };
          return (
            <line
              key={`ck${k}`}
              x1={p.x}
              y1={p.y}
              x2={q.x}
              y2={q.y}
              stroke="var(--color-akcent)"
              strokeOpacity={0.55}
              strokeWidth={DOTYK}
              strokeLinecap="round"
              className="cel-pulsuje"
              onClick={() => kliknijDroge(k)}
            />
          );
        })}

        {/* Postawione osady i miasta */}
        {view.osady.map((osada) => {
          const p = g.wierzcholki[osada.w] as { x: number; y: number };
          const r = g.promien * (osada.miasto ? 0.22 : 0.16);
          return (
            <circle
              key={`o${osada.w}`}
              cx={p.x}
              cy={p.y}
              r={r}
              fill={barwa(osada.gracz)}
              stroke="var(--pionek-obrys)"
              strokeWidth={0.4}
            />
          );
        })}

        {/* Cele osad */}
        {widoczneOsady.map((w) => {
          const p = g.wierzcholki[w] as { x: number; y: number };
          return (
            <g key={`cw${w}`} className="cel-pulsuje" onClick={() => kliknijOsade(w)}>
              <circle cx={p.x} cy={p.y} r={DOTYK} fill="transparent" />
              <circle cx={p.x} cy={p.y} r={g.promien * 0.14} fill="var(--color-akcent)" />
            </g>
          );
        })}

        {/* Cele miast — pierścień wokół własnej osady */}
        {widoczneMiasta.map((w) => {
          const p = g.wierzcholki[w] as { x: number; y: number };
          return (
            <g
              key={`cm${w}`}
              className="cel-pulsuje"
              onClick={() => {
                onBuduj("miasto", w);
                setTryb(null);
              }}
            >
              <circle cx={p.x} cy={p.y} r={DOTYK} fill="transparent" />
              <circle
                cx={p.x}
                cy={p.y}
                r={g.promien * 0.28}
                fill="none"
                stroke="var(--color-akcent)"
                strokeWidth={0.8}
              />
            </g>
          );
        })}
      </svg>

      {/* --- Przyciski zawieszone przy krawędzi planszy --------------------- */}
      {wAkcji && (
        <>
          {/* Przy górnej krawędzi: w środku wysokości zasłaniały heksy. */}
          <div className="absolute right-1 top-1 flex flex-col gap-2">
            <button
              type="button"
              onClick={() => setPanel(panel === "rozwoj" ? null : "rozwoj")}
              aria-label="Karty rozwoju"
              className={[
                "min-h-14 w-14 rounded-2xl border border-obrys shadow-lg",
                panel === "rozwoj" ? "bg-akcent text-tlo" : "bg-panel-jasny",
              ].join(" ")}
            >
              <span className="block text-xl">🂠</span>
              <span className="block text-xs tabular-nums">{view.mojeKarty.length}</span>
            </button>
            <button
              type="button"
              onClick={() => {
                setPanel(panel === "handel" ? null : "handel");
                setHandelZ(null);
              }}
              aria-label="Wymiana w banku"
              className={[
                "min-h-14 w-14 rounded-2xl border border-obrys shadow-lg",
                panel === "handel" ? "bg-akcent text-tlo" : "bg-panel-jasny",
              ].join(" ")}
            >
              <span className="block text-xl">⇄</span>
              <span className="block text-xs">bank</span>
            </button>
          </div>

          <div className="absolute left-1 bottom-1">
            <Przycisk wariant="glowny" onClick={onKoniecTury} className="shadow-lg">
              Koniec tury
            </Przycisk>
          </div>
        </>
      )}

      {/* --- Wysuwany panel nad planszą ------------------------------------- */}
      {wAkcji && panel && (
        <div className="absolute inset-x-1 bottom-1 rounded-2xl border border-obrys bg-panel p-3 shadow-xl">
          {panel === "rozwoj" && (
            <>
              <div className="mb-2 flex items-center justify-between">
                <span className="text-tekst-slaby">Karty rozwoju</span>
                <button type="button" onClick={() => setPanel(null)} className="px-2 text-xl">
                  ×
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {/*
                  * Cena na przycisku, liczba pozostałych kart osobno: samo
                  * „Kup (25)" czytało się jak cena, a jest zapasem talii.
                  */}
                <Przycisk
                  onClick={onKupRozwoj}
                  disabled={view.taliaZostalo === 0}
                  className="min-h-12 flex flex-col justify-center gap-1 px-3 py-1.5 text-base"
                >
                  <span>Kup kartę</span>
                  <Koszt koszt={view.kosztRozwoju} reka={view.mojaReka} />
                </Przycisk>
                <span className="self-center text-sm text-tekst-slaby">
                  w talii: {view.taliaZostalo}
                </span>
                {view.mojeKarty.map((karta, i) =>
                  /*
                   * Karta punktowa nie jest przyciskiem, tylko odznaką: liczy
                   * się sama od chwili kupienia i nie ma czego zagrywać.
                   * Wyszarzony przycisk w rzędzie klikalnych kart czytał się
                   * jak „zepsute" albo „jeszcze nie teraz", a znaczy „to już
                   * działa" — stąd inny kształt, nie inny stan.
                   */
                  karta === "punkt" ? (
                    <span
                      key={`${karta}-${i}`}
                      className="flex min-h-12 items-center gap-2 rounded-2xl border border-obrys bg-panel-jasny px-3 text-base"
                    >
                      🏆 {NAZWA_KARTY[karta]}
                      <span className="text-sm text-tekst-slaby">liczy się sam</span>
                    </span>
                  ) : (
                    <Przycisk
                      key={`${karta}-${i}`}
                      wariant="gorny"
                      onClick={() => {
                        if (WYMAGA_WYBORU.has(karta)) setWyborKarty(karta);
                        else onZagrajRozwoj(karta);
                        setPanel(null);
                      }}
                      className="text-base"
                    >
                      {NAZWA_KARTY[karta] ?? karta}
                    </Przycisk>
                  ),
                )}
                {view.mojeKarty.length === 0 && (
                  <span className="self-center text-tekst-slaby">Nie masz jeszcze kart.</span>
                )}
              </div>
            </>
          )}

          {panel === "handel" && (
            <>
              <div className="mb-2 flex items-center justify-between">
                <span className="text-tekst-slaby">
                  {handelZ
                    ? `Oddajesz ${view.kursy[handelZ] ?? 4}× ${SKROT[handelZ]} — co bierzesz?`
                    : "Co oddajesz?"}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setPanel(null);
                    setHandelZ(null);
                  }}
                  className="px-2 text-xl"
                >
                  ×
                </button>
              </div>
              <div className="grid grid-cols-5 gap-2">
                {SUROWCE.map((surowiec) => {
                  const kurs = view.kursy[surowiec] ?? 4;
                  const stac = (view.mojaReka[surowiec] ?? 0) >= kurs;
                  const wybieramCel = handelZ !== null;
                  return (
                    <button
                      key={surowiec}
                      type="button"
                      disabled={
                        wybieramCel
                          ? surowiec === handelZ || (view.bank[surowiec] ?? 0) < 1
                          : !stac
                      }
                      onClick={() => {
                        if (!wybieramCel) setHandelZ(surowiec);
                        else {
                          onHandel(handelZ, surowiec);
                          setHandelZ(null);
                          setPanel(null);
                        }
                      }}
                      className={[
                        "rounded-xl border border-obrys py-2 disabled:opacity-30",
                        handelZ === surowiec ? "bg-akcent" : "bg-panel-jasny",
                      ].join(" ")}
                    >
                      <span
                        className="mx-auto block h-2 w-8 rounded-full"
                        style={{ backgroundColor: `var(--surowiec-${surowiec})` }}
                      />
                      <span className="mt-1 block text-sm tabular-nums">
                        {wybieramCel ? "+1" : `${kurs}:1`}
                      </span>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
      </div>

      {/* --- Odrzucanie: blokuje wszystko inne, więc stoi najwyżej ---------- */}
      {view.muszeOdrzucic > 0 && (
        <Karta>
          <p className="mb-2 text-lg">
            Odrzuć {view.muszeOdrzucic} kart — wybrano {suma(wybrane)}.
          </p>
          <div className="grid grid-cols-5 gap-2">
            {SUROWCE.map((surowiec) => {
              const mam = view.mojaReka[surowiec] ?? 0;
              const oddane = wybrane[surowiec] ?? 0;
              return (
                <button
                  key={surowiec}
                  type="button"
                  disabled={oddane >= mam || suma(wybrane) >= view.muszeOdrzucic}
                  onClick={() => setWybrane({ ...wybrane, [surowiec]: oddane + 1 })}
                  className="rounded-xl border border-obrys bg-panel-jasny py-2 disabled:opacity-40"
                >
                  <span
                    className="mx-auto block h-2 w-8 rounded-full"
                    style={{ backgroundColor: `var(--surowiec-${surowiec})` }}
                  />
                  <span className="mt-1 block text-lg tabular-nums">
                    {mam - oddane}
                    {oddane > 0 ? ` −${oddane}` : ""}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="mt-3 flex gap-2">
            <Przycisk wariant="cichy" onClick={() => setWybrane({})}>
              Wyczyść
            </Przycisk>
            <Przycisk
              wariant="glowny"
              pelna
              disabled={suma(wybrane) !== view.muszeOdrzucic}
              onClick={() => {
                onOdrzuc(wybrane);
                setWybrane({});
              }}
            >
              Odrzuć
            </Przycisk>
          </div>
        </Karta>
      )}

      {/* --- Kto okradziony ------------------------------------------------ */}
      {wyborOfiary && (
        <Karta>
          <p className="mb-2 text-lg">Kogo okraść?</p>
          <div className="flex flex-wrap gap-2">
            {wyborOfiary.kandydaci.map((id) => (
              <Przycisk
                key={id}
                wariant="glowny"
                onClick={() => {
                  onZlodziej(wyborOfiary.heks, id);
                  setWyborOfiary(null);
                }}
              >
                {nicki[id] ?? id}
              </Przycisk>
            ))}
          </div>
        </Karta>
      )}

      {/* --- Przekaz od kogoś ---------------------------------------------- */}
      {view.przekazDoMnie && (
        <Karta>
          <p className="mb-2 text-lg">
            {nicki[view.przekazDoMnie.od] ?? "Ktoś"} przekazuje Ci surowce.
          </p>
          <Przycisk wariant="glowny" pelna onClick={onPrzyjmij}>
            Przyjmij
          </Przycisk>
        </Karta>
      )}

      {/* --- Status + kości ------------------------------------------------ */}
      <div className="flex items-center justify-between gap-3">
        <p className={view.mojaTura ? "text-akcent text-lg" : "text-tekst-slaby"}>
          {status(view, nicki)}
        </p>
        {view.kosci && (
          <div className="flex items-center gap-2">
            {/* Oczka w barwie tego, kto rzucał — widać czyja to była tura. */}
            <Kostka wartosc={view.kosci[0]} rozmiar="mala" kolor={barwa(view.naRuchu ?? "")} />
            <Kostka wartosc={view.kosci[1]} rozmiar="mala" kolor={barwa(view.naRuchu ?? "")} />
            <span className="text-2xl tabular-nums">{view.kosci[0] + view.kosci[1]}</span>
          </div>
        )}
      </div>

      {view.canStart && (
        <Przycisk wariant="glowny" pelna onClick={onStart} className="min-h-16 text-xl">
          Zaczynamy
        </Przycisk>
      )}

      {view.mojaTura && view.phase === "rzut" && (
        <Przycisk wariant="glowny" pelna onClick={onRzut} className="min-h-16 text-xl">
          Rzuć kośćmi
        </Przycisk>
      )}

      {/* --- Ręka ---------------------------------------------------------- */}
      {view.phase !== "lobby" && (
        <div className="grid grid-cols-5 gap-2">
          {SUROWCE.map((surowiec) => (
            <KartaSurowca
              // Klucz z licznikiem przyrostów: remount odpala animację nawet
              // wtedy, gdy przybyło dokładnie tyle samo co poprzednio.
              key={`${surowiec}-${przyrosty.ile[surowiec] ? przyrosty.licznik : 0}`}
              surowiec={surowiec}
              ile={view.mojaReka[surowiec] ?? 0}
              przyrost={przyrosty.ile[surowiec] ?? 0}
            />
          ))}
        </div>
      )}

      {/*
        * --- Budowanie ------------------------------------------------------
        *
        * Przyciski stoją przez całą partię, także w cudzej turze: to jedyne
        * miejsce, w którym widać cennik, a dzieci pytały o niego wtedy, gdy
        * właśnie nie były na ruchu. Nieaktywne są przygaszone stylem, a nie
        * klasą `disabled:opacity-40` — przy niej cennik przestawał być czytelny,
        * czyli znikało to, po co przycisk w ogóle stoi.
        */}
      {view.phase !== "lobby" && view.phase !== "koniec" && (
        <>
          <div className="grid grid-cols-3 gap-2">
            {BUDOWLE.map((co) => {
              const mozliwe =
                view.mojaTura && view.phase === "akcje" && doWyboru[co].length > 0;
              return (
                <Przycisk
                  key={co}
                  wariant={tryb === co ? "glowny" : "zwykly"}
                  disabled={!mozliwe}
                  onClick={() => setTryb(tryb === co ? null : co)}
                  style={mozliwe ? undefined : { opacity: 0.75 }}
                  className="min-h-12 flex flex-col justify-center gap-1 px-2 py-1.5 text-base"
                >
                  <span>{NAZWA_BUDOWLI[co]}</span>
                  <Koszt koszt={view.koszty[co]} reka={view.mojaReka} />
                </Przycisk>
              );
            })}
          </div>

          {tryb && (
            <p className="text-center text-tekst-slaby">
              Dotknij podświetlonego miejsca na planszy ({doWyboru[tryb].length} do wyboru).
            </p>
          )}

        </>
      )}

      {/* --- Wybór surowca do karty ---------------------------------------- */}
      {wyborKarty && (
        <Karta>
          <p className="mb-2 text-lg">
            {wyborKarty === "monopol" ? "Który surowiec zabrać wszystkim?" : "Co wziąć z banku?"}
          </p>
          <div className="grid grid-cols-5 gap-2">
            {SUROWCE.map((surowiec) => (
              <button
                key={surowiec}
                type="button"
                onClick={() => {
                  // Urodzaj bierze dwie sztuki tego samego — prościej niż dwa kroki.
                  onZagrajRozwoj(
                    wyborKarty,
                    wyborKarty === "monopol" ? { surowiec } : { surowce: [surowiec, surowiec] },
                  );
                  setWyborKarty(null);
                }}
                className="rounded-xl border border-obrys bg-panel-jasny py-2"
              >
                <span
                  className="mx-auto block h-2 w-8 rounded-full"
                  style={{ backgroundColor: `var(--surowiec-${surowiec})` }}
                />
                <span className="mt-1 block text-xs">{SKROT[surowiec]}</span>
              </button>
            ))}
          </div>
          <Przycisk wariant="cichy" className="mt-2" onClick={() => setWyborKarty(null)}>
            Anuluj
          </Przycisk>
        </Karta>
      )}

      {/* --- Gracze --------------------------------------------------------- */}
      <ul className="flex flex-col gap-1">
        {view.players.map((id) => {
          const karty = view.kartyGraczy[id] ?? 0;
          const rozwoj = view.kartyRozwojuGraczy[id] ?? 0;
          const rycerze = view.rycerze[id] ?? 0;
          const droga = view.dlugoscDrogi[id] ?? 0;
          // Powyżej siedmiu kart siódemka boli — to jedyna liczba na tej
          // liście, która zmienia czyjeś decyzje, więc jest wytłuszczona.
          const nadLimitem = karty >= 8;
          const maDroge = view.najdluzszaDrogaU === id;

          return (
            <li key={id} className="rounded-xl border-l-4 bg-panel-jasny" style={{ borderLeftColor: barwa(id) }}>
              <button
                type="button"
                onClick={() => setRozwiniety(rozwiniety === id ? null : id)}
                aria-expanded={rozwiniety === id}
                className="flex w-full items-center gap-2 px-3 py-2 text-left"
              >
                <span className="flex-1 truncate">
                  {nicki[id] ?? id}
                  {id === mojeId ? " (Ty)" : ""}
                </span>
                <span className="text-xl tabular-nums">{view.punkty[id] ?? 0}</span>
                {/*
                  * Skróty zamiast słów: cztery liczby po polsku nie mieszczą się
                  * w wierszu na telefonie, a rozwinięcie niżej i tak je nazywa.
                  */}
                <span className="flex gap-1.5 text-sm text-tekst-slaby tabular-nums">
                  <span className={nadLimitem ? "font-bold text-tekst" : ""}>{karty}K</span>
                  <span>{rozwoj}Rz</span>
                  <span>{rycerze}Ry</span>
                  <span className={maDroge ? "font-bold text-tekst" : ""}>{droga}D</span>
                </span>
              </button>

              {rozwiniety === id && (
                <dl className="grid grid-cols-2 gap-x-3 gap-y-1 border-t border-obrys px-3 py-2 text-sm">
                  <dt className="text-tekst-slaby">Punkty zwycięstwa</dt>
                  <dd className="text-right tabular-nums">
                    {view.punkty[id] ?? 0} z {view.celPZ}
                  </dd>

                  <dt className="text-tekst-slaby">Karty surowców</dt>
                  <dd className="text-right tabular-nums">
                    {karty}
                    {nadLimitem ? " — siódemka zabierze połowę" : ""}
                  </dd>

                  <dt className="text-tekst-slaby">Karty rozwoju</dt>
                  <dd className="text-right tabular-nums">{rozwoj}</dd>

                  <dt className="text-tekst-slaby">Zagrani rycerze</dt>
                  <dd className="text-right tabular-nums">
                    {rycerze}
                    {view.najwiekszaArmiaU === id ? " — największa armia" : ""}
                  </dd>

                  <dt className="text-tekst-slaby">Najdłuższa droga</dt>
                  <dd className="text-right tabular-nums">
                    {droga}
                    {maDroge ? " — premia jego" : ""}
                  </dd>
                </dl>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
